import { format, parseISO } from 'date-fns'

import { getFilter } from '@/service/filter'

import { useAppStore } from '@/store/app'

import type { FilterComponentState, QueryStringFilterState } from '@/types/filters'
import { FilterTypeEnum, FilterUiTypeEnum, FilterTagIdEnum, CurrencyEnum, ModeQueryStringEnum } from '@/types/filters'
import { SortRuleEnum } from '@/types/product'
import type { SfilterFilterTag } from '@/types/api'

export const DEFAULT_FILTER_STATE: FilterComponentState = {
  [FilterTypeEnum.ORDER]: SortRuleEnum.POPULAR,
  [FilterTypeEnum.AGE]: [],
  [FilterTypeEnum.DATE]: [null, null],
  [FilterTypeEnum.PRICE]: [null, null],
}

export async function useSearchFilter() {
  const { t } = useI18n()
  const app = useAppStore()
  const route = useRoute()
  const router = useLocaleRouter()

  const regionId = computed(() => Number(route.params.rid))
  const categoryId = computed(() => (route.params.cid || route.params.tid) as string)

  // private util functions
  const _updateFilterState = <FilterTypeEnum extends keyof FilterComponentState>(
    key: FilterTypeEnum,
    value: FilterComponentState[FilterTypeEnum]
  ) => {
    if (key in filterState.value) {
      filterState.value[key] = value
    }
  }

  const _getQueryStringFilterState = (): QueryStringFilterState => {
    const queryFilter = route.query.filter ? (JSON.parse(route.query.filter as string) as QueryStringFilterState) : {}
    const { min_price, max_price, start_date, end_date, age, ...restFilter } = queryFilter
    const price = [null, null] as [Nullable<number>, Nullable<number>]
    if (min_price) price[0] = Number(min_price)
    if (max_price) price[1] = Number(max_price)

    const date = [null, null] as [Nullable<Date>, Nullable<Date>]
    if (start_date) date[0] = parseISO(start_date)
    if (end_date) date[1] = parseISO(end_date)

    return {
      ...(age ? { age: age as number[] } : {}),
      price,
      date,
      ...restFilter,
    }
  }

  const _updateQueryString = () => {
    const queries = cloneDeep(route.query)
    queries.filter = JSON.stringify(
      trimObject({
        age: filterState.value.age,
        min_price: filterState.value.price[0],
        max_price: filterState.value.price[1],
        start_date: filterState.value.date[0] ? format(filterState.value.date[0], 'yyyy-MM-dd') : null,
        end_date: filterState.value.date[1] ? format(filterState.value.date[1], 'yyyy-MM-dd') : null,
        ...restFilterTags.value.reduce(
          (acc, cur) => {
            const key = `${cur.id}`
            if (key) acc[key] = filterState.value[key] as number[]
            return acc
          },
          {} as Record<string, number[]>
        ),
      }) as QueryStringFilterState
    )

    router.push({ query: queries })
  }

  const filterOptionsParams = computed(() => ({
    region_id: regionId.value,
    ...(categoryId.value
      ? {
          category_id: categoryId.value,
        }
      : {}),
  }))

  const { data: filterOptions } = await useAsyncData(
    `filter_${regionId.value}_${categoryId.value}`,
    async () => {
      const { data } = await getFilter(filterOptionsParams.value)
      return data
    },
    {
      watch: [filterOptionsParams],
      lazy: true,
      server: false,
    }
  )

  // data sort options
  const filterOrderOptions = computed(() => [
    {
      label: t('sort_by_popular'),
      value: SortRuleEnum.POPULAR,
    },
    ...(app.geolocation.locatedAt
      ? [
          {
            label: t('sort_by_distance'),
            value: SortRuleEnum.DISTANCE,
          },
        ]
      : []),
    {
      label: t('sort_by_price_low_to_high'),
      value: SortRuleEnum.PRICE_LOW,
    },
    {
      label: t('sort_by_price_high_to_low'),
      value: SortRuleEnum.PRICE_HIGH,
    },
  ])

  // price range
  const currencyId = computed(() => filterOptions.value?.currency_id || CurrencyEnum.TWD)
  const priceRangeLimit = computed(() => filterOptions.value?.components?.find(i => i.name === 'price')?.options?.map(j => Number(j.value)) || [])
  const sliderStep = computed(() => {
    switch (filterOptions.value?.currency_id) {
      case CurrencyEnum.JPY:
        return 100
      case CurrencyEnum.THB:
      case CurrencyEnum.TWD:
        return 10
      case CurrencyEnum.HKD:
      case CurrencyEnum.MYR:
      default:
        return 1
    }
  })
  const priceLabelFormatter = (value: number) =>
    `${CurrencyEnum[currencyId.value]} ${priceFormat(value || priceRangeLimit.value[0], app.locale)}${value === priceRangeLimit.value[1] ? '+' : ''}`

  const restFilterTags = computed(() => filterOptions.value?.filter_tags?.filter(i => ![FilterTagIdEnum.AGE].includes(Number(i.id))) || [])

  const filterItemOption = computed<Record<string, any>>(() => {
    const restFilterOptions = restFilterTags.value?.reduce(
      (acc, c) => {
        const key = `${c.id}`
        acc[key] = {
          options: c,
        }
        return acc
      },
      {} as Record<string, { options: SfilterFilterTag }>
    )
    return {
      [FilterTypeEnum.ORDER]: {
        options: filterOrderOptions.value,
      },
      [FilterTypeEnum.DATE]: {
        dayMode: filterOptions.value?.day_mode,
      },
      [FilterTypeEnum.PRICE]: {
        currencyId: currencyId.value,
        range: priceRangeLimit.value,
        step: sliderStep.value,
        min: priceRangeLimit.value[0],
        max: priceRangeLimit.value[1],
        formatter: priceLabelFormatter,
      },
      [FilterTypeEnum.AGE]: {
        options: filterOptions.value?.filter_tags?.find(i => i.id === FilterTagIdEnum.AGE),
      },
      ...restFilterOptions,
    }
  })

  const filterLayout = computed(() => [
    ...(categoryId.value && filterItemOption.value[FilterTypeEnum.AGE].options
      ? [
          {
            name: FilterTypeEnum.AGE,
            label: t('filter_age'),
            icon: 'age',
            type: FilterUiTypeEnum.CHECK_BOX,
            clearable: true,
          },
        ]
      : []),
    {
      name: FilterTypeEnum.DATE,
      label: t('filter_date'),
      icon: 'calendar',
      type: FilterUiTypeEnum.DATE_RANGE,
      clearable: true,
    },
    {
      name: FilterTypeEnum.PRICE,
      label: t('filter_price'),
      icon: 'money',
      type: FilterUiTypeEnum.RANGE,
      clearable: true,
    },
    ...restFilterTags.value?.map(i => ({
      name: `${i.id}` || '',
      label: i.title,
      icon: '',
      type: FilterUiTypeEnum.CHECK_BOX,
      clearable: true,
    })),
  ])

  // state management
  const baseFilterState: FilterComponentState = {
    ...DEFAULT_FILTER_STATE,
    ...{
      order: route.query.mode !== ModeQueryStringEnum.POPULAR_LIST_KEY && app.geolocation.locatedAt ? SortRuleEnum.DISTANCE : SortRuleEnum.POPULAR,
    },
    ...restFilterTags.value.reduce(
      (acc, cur) => {
        if (cur.id) acc[cur.id] = []
        return acc
      },
      {} as Record<string, number[]>
    ),
  }

  const filterState = ref({
    ...cloneDeep(baseFilterState),
    ..._getQueryStringFilterState(),
  })

  const lastSubmitFilterState = ref(cloneDeep(filterState.value))

  const filterStateUnWrap = computed(() => ({
    sortby: filterState.value.order,
    filter_tags: trimNestedArray([
      filterState.value.age,
      ...restFilterTags.value.map(t => {
        const key = `${t.id}`
        return (filterState.value[key] as number[]) || []
      }),
    ]),
    ...(filterState.value.price[0] ? { min_price: filterState.value.price[0] } : {}),
    ...(filterState.value.price[1] ? { max_price: filterState.value.price[1] } : {}),
    ...(filterState.value.date[0] ? { starttime: format(filterState.value.date[0], 'yyyy-MM-dd') } : {}),
    ...(filterState.value.date[1] ? { endtime: format(filterState.value.date[1], 'yyyy-MM-dd') } : {}),
  }))

  const isOrderChanged = computed(() => filterState.value.order !== lastSubmitFilterState.value.order)

  const clear = (key: string) => _updateFilterState(key, cloneDeep(baseFilterState[key]))
  const reset = (key: string) => _updateFilterState(key, cloneDeep(lastSubmitFilterState.value[key]))
  const saveLastSubmitted = () => {
    _updateQueryString()
    lastSubmitFilterState.value = cloneDeep(filterState.value)
  }

  return {
    filterItemOption,
    filterLayout,

    filterState,
    filterStateUnWrap,
    isOrderChanged,

    reset,
    clear,
    saveLastSubmitted,
  }
}
